import "../App.css";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";

import PersonalDetailsSection from "../components/PersonalDetailsSection";

import { useNavigate } from "react-router-dom";

import type {
  Profile,
  SideProject,
  SectionTypeDescription,
  ProfileLinkConfig,
  Branding,
  AccountProfile,
  ProjectPhoto,
  ProfileContactDetails,
  ContentSuggestionChatMessage,
  ContentSuggestionRequestBody,
  ProfileLinkResponseBody,
} from "../types";
import {
  createGuestUser,
  createProfileLinkQuery,
  fetchProfileFromLinkId,
  updateProfileVisit,
} from "../requests";
import SideProjectSection from "../components/SideProjectSection";

import "../components/components.css";
import { darkModeBlack } from "../colors";
import { useMediaQuery } from "react-responsive";
import { Dialog } from "@material-ui/core";
import ProjectPhotoViewer from "../components/ProjectPhotoViewer";
import WorkplaceSection from "../components/WorkplaceSection";
import EditorTitle from "../components/EditorTitle";
import { useParams } from "react-router-dom";
import { LeadButton } from "./dashboard/home/components/LeadButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faAt,
  faLink,
  faComment,
  faFilePdf,
  faPaintBrush,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import vocsLogo from "../assets/voc_studio_logo.png";

import { toast } from "sonner";

import {
  Input,
  trim,
  InputRef,
  Button,
  LoadingSpinner,
  Image,
} from "../components";

import { motion } from "framer-motion";
import QRCode from "react-qr-code";

import EducationSection from "../components/EducationSection";
import SkillsSection from "../components/SkillsSection";

import ProfileContainer from "./profile/ProfileContainer";
import { useStatsigClient } from "../hooks";
import { SalesContactDrawer } from "./profile/SalesContactDrawer";
import { salesContactState } from "../state/salesContact";
import PDFProfileContainer from "./profile/PDFProfileContainer";
import { useThemeColors } from "../contexts";

interface RouteParams {
  [key: string]: string | undefined;
  profileLinkId?: string;
}

const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    console.warn("Invalid hex color provided to hexToRgba:", hex);
    return `rgba(0, 0, 0, ${alpha})`; // Fallback to black with specified alpha
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function PDFProfileViewer() {
  const { profileLinkId } = useParams<RouteParams>();

  const { primary } = useThemeColors();

  const navigate = useNavigate();
  const [darkModeActive, setDarkModeActive] = useState<boolean>(false);
  const [pdfMode, setPdfMode] = useState<boolean>(false);

  const [profileLink, setProfileLink] = useState("");
  const [linkCreatorProfileId, setLinkCreatorProfileId] = useState("");
  const [viewDialogFullScreen, setViewDialogFullScreen] =
    useState<boolean>(false);
  const [isManager, setIsManager] = useState<boolean>(false);
  const [profilePhotoId, setProfilePhotoId] = useState<string>("");
  const [accountProfile, setAccountProfile] = useState<AccountProfile | null>(
    null,
  );

  const statsigClient = useStatsigClient();

  const [selectedProjectPhotoAssetId, setSelectedProjectPhotoAssetId] =
    useState<string>("");
  const [profileLinkConfig, setProfileLinkConfig] =
    useState<ProfileLinkConfig | null>(null);
  const [profileLinkResponseBodies, setProfileLinkResponseBodies] = useState<
    ProfileLinkResponseBody[]
  >([]);

  const [currentProfileSideProjects, setCurrentProfileSideProjects] = useState<
    SideProject[]
  >([]);
  const [profileContactDetails, setProfileContactDetails] =
    useState<ProfileContactDetails | null>(null);
  const [profile, setProfile] = useState<Profile>({
    id: "",
    profile_name: "",
    name: "",
    title: "",
    pitch: "",
    ui_config: { dark_mode: false, use_profile_picture: true },
    roles_section: { order: 0, title: "", roles: [] },
    skills_section: { order: 0, title: "", skill_blocks: [] },
    side_projects_section: { order: 0, title: "", side_projects: [] },
    profile_tags: [],
    workplaces: [],
    educations: [],
  });

  const isMobile = useMediaQuery({ query: "(max-width: 760px)" });

  const inputRef = useRef<InputRef>(null);
  const profileLinkQuestionToastId = useRef<string | number>();
  const [additionalPitches, setAdditionalPitches] = useState<string[]>([]);
  const [profileNotAvailable, setProfileNotAvailable] =
    useState<boolean>(false);

  const [currentQuestion, setCurrentQuestions] = useState<string>("");
  const [currentAnswer, setCurrentAnswer] = useState<string>("");
  const [isLoadingProfileLinkQuestion, setIsLoadingProfileLinkQuestion] =
    useState<boolean>(false);

  const firstRender = useRef(true);

  const handleAddComment = useCallback(
    (value: string) => {
      setCurrentQuestions(value);
      let messages: ContentSuggestionChatMessage[] = [
        { order: 0, content: value, fromUser: true },
      ];
      if (profileLinkId) {
        profileLinkQuestionToastId.current = toast.loading(
          "Checking profile for answers...",
        );
        setIsLoadingProfileLinkQuestion(true);

        createProfileLinkQuery(profileLinkId, messages).then(
          (resp: ContentSuggestionRequestBody) => {
            toast.success("Done!", { id: profileLinkQuestionToastId.current });
            setCurrentAnswer(resp.messages[1].content);
            setIsLoadingProfileLinkQuestion(false);
          },
        );
      }

      inputRef.current?.clear();
    },
    [profileLinkId],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (inputRef.current?.value) handleAddComment(inputRef.current?.value);
    },
    [handleAddComment],
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const pdfParam = queryParams.get("pdf");
    setPdfMode(pdfParam === "true");

    if (profileLinkId !== undefined) {
      fetchProfileFromLinkId(profileLinkId)
        .then((profileLinkResponseBody) => {
          const accountProfile = profileLinkResponseBody.accountProfile;
          setProfileContactDetails(
            profileLinkResponseBody.profileContactDetails,
          );
          setProfile(accountProfile.profile);
          setProfilePhotoId(accountProfile.profilePhotoId ?? "");
          setLinkCreatorProfileId(accountProfile.linkCreatedByProfileId ?? "");
          setProfileLink(profileLinkId);
          setAccountProfile(accountProfile);
          setIsManager(accountProfile.isManager ?? false);
          if (accountProfile.linkConfig) {
            setDarkModeActive(accountProfile.linkConfig.darkMode);
            setProfileLinkConfig(accountProfile.linkConfig);
            if (accountProfile.linkConfig.dynamic) {
              if (localStorage.getItem("op-guest-user-id") != null) {
                navigate(`/links/${profileLinkId}?dynamic=true`);
              } else {
                createGuestUser().then((guestUserId) => {
                  localStorage.setItem("op-guest-user-id", guestUserId);
                  navigate(`/links/${profileLinkId}?dynamic=true`);
                });
              }
            }

            let additional: string[] = [];

            if (
              accountProfile.linkConfig.showAssignmentPitch &&
              accountProfile.linkConfig.assignmentPitch !== ""
            ) {
              additional.push(accountProfile.linkConfig.assignmentPitch);
            }

            if (
              accountProfile.linkConfig.showCustomPitch &&
              accountProfile.linkConfig.customPitch !== ""
            ) {
              additional.push(accountProfile.linkConfig.customPitch);
            }

            setAdditionalPitches(additional);
          }

          const sortedSideProjects =
            accountProfile.profile.side_projects_section?.side_projects?.sort(
              (a: SideProject, b: SideProject) => {
                return a.order - b.order;
              },
            ) || [];

          setCurrentProfileSideProjects(sortedSideProjects);
        })
        .catch((err) => {
          if (err.response.data === "LINK_ARCHIVED\n") {
            setProfileNotAvailable(true);
          }
        });
    }
  }, []);

  function getAssetUrl(branding: Branding | null) {
    if (branding === null) return null;
    if (branding.logotypes.length > 0) {
      return branding.logotypes[0].assetUrl;
    }
    return null;
  }

  const renderContactInformation = () => {
    return (
      <div className="text-xs">
        <p className="font-semibold">{profileContactDetails?.salesContact}</p>
        <p className="text-gray-6">{profileContactDetails?.email}</p>
        <p className="text-gray-6">{profileContactDetails?.phoneNumber}</p>
      </div>
    );
  };

  const shouldDisplayTopContactInformation = () => {
    if (linkConfig.brandingAndContactConfig) {
      if (linkConfig.brandingAndContactConfig.displayTopContact === undefined) {
        return true;
      }
      return (
        linkConfig.brandingAndContactConfig &&
        linkConfig.brandingAndContactConfig.displayTopContact
      );
    }
    return true;
  };

  const shouldDisplayTopContainer = () => {
    if (linkConfig.brandingAndContactConfig) {
      if (linkConfig.brandingAndContactConfig.displayTopContact === undefined) {
        return true;
      }
      return linkConfig.brandingAndContactConfig.displayTopContact;
    }
    return true;
  };

  const shouldRenderQRCode = () => {
    if (linkConfig.brandingAndContactConfig) {
      return linkConfig.brandingAndContactConfig.displayQRLink;
    }
    return true;
  };

  const shouldDisplayTopLogo = () => {
    if (linkConfig.brandingAndContactConfig) {
      if (linkConfig.brandingAndContactConfig.displayTopContact === undefined) {
        return true;
      }
      return (
        linkConfig.brandingAndContactConfig &&
        linkConfig.brandingAndContactConfig.displayTopLogo
      );
    }
    return true;
  };

  const shouldDisplayBottomLogo = () => {
    return (
      linkConfig.brandingAndContactConfig &&
      linkConfig.brandingAndContactConfig.displayBottomLogo
    );
  };

  const shouldDisplayBottomContact = () => {
    return (
      linkConfig.brandingAndContactConfig &&
      linkConfig.brandingAndContactConfig.displayBottomContact
    );
  };

  const shouldDisplayBottomContainer = () => {
    return (
      linkConfig.brandingAndContactConfig &&
      (linkConfig.brandingAndContactConfig.displayBottomContact ||
        linkConfig.brandingAndContactConfig.displayBottomLogo)
    );
  };

  if (profileNotAvailable) {
    return (
      <div className="w-full flex items-center justify-center h-full flex-col">
        <h3 className="font-medium text-xl">
          Profile is no longer available for viewing
        </h3>
        <img className="max-w-44 mt-4" src={vocsLogo} alt="VOCS" />
      </div>
    );
  }

  if (profileLinkConfig === null) {
    return (
      <div className="flex flex-1 h-[800px] flex-col justify-center align-center ">
        <div>
          <LoadingSpinner size="medium" />
        </div>
      </div>
    );
  }
  const linkConfig = profileLinkConfig;
  const logoAssetUrl = getAssetUrl(linkConfig.branding || null);

  return (
    <div id="pdf-wrapper">
      <div className="profile-viewer-content">
        {shouldDisplayTopContainer() && (
          <div
            className="flex justify-between items-center border-2 rounded-2xl py-4 px-6 mb-10"
            style={{
              borderColor: shouldDisplayTopContainer()
                ? hexToRgba(primary, 0.2)
                : "transparent",
            }}
          >
            {shouldDisplayTopContactInformation() ? (
              renderContactInformation()
            ) : (
              <div></div>
            )}
            {shouldDisplayTopLogo() && shouldDisplayTopContactInformation() && (
              <img
                alt="logo"
                src={
                  "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/" +
                  profileContactDetails?.logoId
                }
                className={`object-scale-contain`}
                style={{
                  maxHeight: 50,
                }}
              />
            )}
          </div>
        )}

        <PDFProfileContainer
          profileLinkId={profileLinkId!}
          isEditable={false}
          pdfMode={pdfMode}
          logoUrl={
            !shouldDisplayTopContainer() && shouldDisplayTopLogo()
              ? "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/" +
                profileContactDetails?.logoId
              : null
          }
        />

        {shouldDisplayBottomContainer() && (
          <div
            className="flex justify-between items-center border-2 rounded-2xl py-4 px-6 mb-10 mt-12"
            style={{
              borderColor: shouldDisplayBottomContainer()
                ? hexToRgba(primary, 0.2)
                : "transparent",
            }}
          >
            {shouldDisplayBottomContact() ? (
              renderContactInformation()
            ) : (
              <div></div>
            )}
            {shouldDisplayBottomLogo() ? (
              <img
                alt="logo"
                src={
                  "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/" +
                  profileContactDetails?.logoId
                }
                className={`object-scale-contain`}
                style={{
                  maxHeight: 50,
                }}
              />
            ) : null}
          </div>
        )}

        {shouldRenderQRCode() && (
          <div className="flex flex-1 flex-row px-4 justify-center items-center mt-12 pb-6 bg-white">
            <QRCode
              size={62}
              value={`https://voc.studio/p/${profileLinkId}`}
              className="rounded-2xl w-16 h-16 mr-4"
            />
            <p>{`Visit ${profile.name}´s profile link!`}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PDFProfileViewer;
